import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { animateScroll as scroll } from 'react-scroll/modules';

import { InfoMessage } from '@eika/attention-message';
import { Checkbox } from '@eika/checkbox';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { ButtonToggles } from '@eika/radio-button';
import { Select } from '@eika/select';

import { FormTypeApplicationCoApplicant } from '../../../actions';
import { DispatchWithState, StringKeyAnyValue, StringKeyValue } from '../../../actions/types';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import Loader from '../../../components/Loader/Loader';
import StepButtons from '../../../components/Steps/StepButtons';
import { ApplicationReducer } from '../../../reducers/application';
import { CalculatorReducer } from '../../../reducers/calculator';
import { LoaderReducer } from '../../../reducers/loader';
import loanType from '../../../util/mappings/loanType';
import { coApplicantSchema } from '../../../validation/loanApplicationSchema';

interface Props {
    action: {
        loanApplicationSecondApplicantNextEvent: (form: FormTypeApplicationCoApplicant) => DispatchWithState;
        loanApplicationSecondApplicantSkipNextEvent: () => DispatchWithState;
        toggleCoApplicant: (hasCoApplicant: boolean) => void;
        openStep: (step: number) => void;
    };
    application: ApplicationReducer;
    calculator: CalculatorReducer;
    form: StringKeyAnyValue;
    loader: LoaderReducer;
    text: StringKeyValue;
}

const LoanApplicantSecond = (props: Props): JSX.Element => {
    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    const { text, application, action, loader, form, calculator } = props;
    const { isSendingApplication, hasSentApplication } = loader;
    const {
        loanApplicationSecondApplicantNextEvent,
        loanApplicationSecondApplicantSkipNextEvent,
        toggleCoApplicant,
        openStep,
    } = action;
    const { coApplicant, hasConfirmedAddress, hasCoApplicant } = application;
    const { rentalFee, grossIncome, totalDebt, tradeIn, tradeInDebt } = coApplicant;

    const hasCoApplicantButtonGroup = [
        {
            label: 'Ja',
            value: '1',
        },
        {
            label: 'Nei',
            value: '0',
        },
    ];

    const {
        handleSubmit,
        register,
        formState: { errors },
        watch,
    } = useForm<FormTypeApplicationCoApplicant>({
        resolver: yupResolver(coApplicantSchema),
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            ...coApplicant,
        },
    });

    const hasError = Object.keys(errors).length > 0;
    const selectedLivingConditionCode = watch().livingConditionCode;
    const selectedTradeIn = watch().tradeIn;
    const formError = <ErrorMessage message={text.formErrorMessage} />;

    if (isSendingApplication || hasSentApplication) return <Loader title={text.loadingSendApplication} />;

    return (
        <div className="co-applicant">
            <div className="co-applicant__selection input-wrapper">
                <label htmlFor="hasCoApplicant">{text.applicantLabelHasCoApplicant}</label>
                <div id="hasCoApplicant">
                    <ButtonToggles
                        label=""
                        options={hasCoApplicantButtonGroup}
                        selectedOption={hasCoApplicant ? '1' : '0'}
                        onChange={(value) => toggleCoApplicant(value === '1')}
                    />
                </div>
            </div>

            {hasCoApplicant ? (
                <form onSubmit={handleSubmit((form) => loanApplicationSecondApplicantNextEvent(form))} id="secondForm">
                    <input type="hidden" {...register('hasCoApplicant')} defaultValue={hasCoApplicant ? '1' : '0'} />

                    <div className="co-applicant__confirm-address input-wrapper">
                        <Checkbox
                            {...register('confirmAddress')}
                            label={text.applicantLabelConfirmCoApplicantAddress}
                            defaultChecked={hasConfirmedAddress}
                        />
                        {errors?.confirmAddress?.message && (
                            <div
                                style={{
                                    marginTop: '12px',
                                    color: '#b83653',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    background: '#FDDEE6',
                                    padding: '4px 8px',
                                    borderRadius: '5px',
                                }}
                            >
                                {String(errors.confirmAddress?.message)}
                            </div>
                        )}
                    </div>

                    <div className="co-applicant__ssn input-wrapper">
                        <Input
                            {...register('ssn')}
                            label={text.applicantLabelSsn}
                            error={errors?.ssn?.message || ''}
                            autoComplete="off"
                            maxLength={11}
                        />
                    </div>

                    <div className="co-applicant__email input-wrapper">
                        <Input
                            {...register('email')}
                            type="email"
                            label={text.applicantLabelEmail}
                            error={errors?.email?.message || ''}
                        />
                    </div>

                    <div className="co-applicant__phone input-wrapper">
                        <Input
                            {...register('phone')}
                            type="tel"
                            label={text.applicantLabelPhone}
                            error={errors?.phone?.message || ''}
                            maxLength={8}
                        />
                    </div>

                    <div className="co-applicant__living-condition input-wrapper">
                        <Select
                            {...register('livingConditionCode')}
                            label={text.applicantLabelLivingCondition}
                            values={form.livingConditionCode.values}
                            error={errors?.livingConditionCode?.message || ''}
                            id="coApplicantLiving"
                        />
                    </div>

                    {selectedLivingConditionCode === 'RENTS_HOUSING' && (
                        <div className="co-applicant__rental-fee input-wrapper">
                            <CurrencyInput
                                {...register('rentalFee')}
                                label={text.applicantLabelRentalFee}
                                placeholder={text.currencyInputPlaceholderKr}
                                defaultValue={rentalFee}
                                error={String(errors?.rentalFee?.message || '')}
                                id="coApplicantRental"
                            />
                        </div>
                    )}

                    <div className="co-applicant__employment input-wrapper">
                        <Select
                            {...register('employmentConditionCode')}
                            label={text.applicantLabelEmployment}
                            values={form.employmentConditionCode.values}
                            error={errors?.employmentConditionCode?.message || ''}
                            id="coApplicantEmployment"
                        />
                    </div>

                    <div className="co-applicant__income input-wrapper">
                        <CurrencyInput
                            {...register('grossIncome')}
                            label={text.applicantCoGrossIncomeLabel}
                            placeholder={text.currencyInputPlaceholderKr}
                            defaultValue={grossIncome}
                            error={String(errors?.grossIncome?.message || '')}
                            max={1000000000}
                        />
                    </div>

                    <div className="co-applicant__debt input-wrapper">
                        <CurrencyInput
                            {...register('totalDebt')}
                            label={text.applicantCoTotalDebtLabel}
                            sublabel={text.applicantCoTotalDebtSubLabel}
                            placeholder={text.currencyInputPlaceholderKr}
                            defaultValue={totalDebt}
                            error={errors?.totalDebt?.message || ''}
                            max={1000000000}
                            id="coApplicantDebt"
                        />
                    </div>

                    {text.applicantTitleTradeIn && (
                        <h3
                            style={{
                                visibility:
                                    calculator.loanInfo.loanType === loanType.loanCommitment ? 'hidden' : 'visible',
                            }}
                        >
                            {text.applicantTitleTradeIn}
                        </h3>
                    )}

                    <div
                        className="co-applicant__trade-in input-wrapper"
                        style={{
                            visibility: calculator.loanInfo.loanType === loanType.loanCommitment ? 'hidden' : 'visible',
                        }}
                    >
                        <Checkbox
                            {...register('tradeIn')}
                            label={text.applicantLabelTradeIn}
                            defaultChecked={tradeIn}
                            error={errors?.tradeIn?.message || ''}
                            id="coApplicantTradeIn"
                        />
                    </div>

                    {selectedTradeIn && (
                        <div className="co-applicant__trade-in-debt input-wrapper">
                            <CurrencyInput
                                {...register('tradeInDebt', {
                                    valueAsNumber: true,
                                })}
                                label={text.applicantLabelTradeInDebt}
                                placeholder={text.currencyInputPlaceholderKr}
                                defaultValue={tradeInDebt}
                                error={errors?.tradeInDebt?.message || ''}
                                id="coApplicantTradeInDebt"
                            />
                        </div>
                    )}

                    <InfoMessage
                        text={text.applicationSubmitInfoBoxCoApplicant}
                        clickableItem={<>{text.applicationSubmitInfoBoxCoApplicant2}</>}
                        icon={IconTypes.LYSPAERE_24}
                    />

                    {hasError && formError}

                    <StepButtons
                        showPrevious
                        previousChangeEvent={() => openStep(3)}
                        nextButtonName={text.applicationSubmitButton}
                        nextButtonType="submit"
                    />
                </form>
            ) : (
                <>
                    <InfoMessage
                        text={text.applicationSubmitInfoBox}
                        clickableItem={<>{text.applicationSubmitInfoBox2}</>}
                        icon={IconTypes.LYSPAERE_24}
                    />
                    <StepButtons
                        showPrevious
                        previousChangeEvent={() => openStep(3)}
                        nextButtonName={text.applicationSubmitButton}
                        nextButtonType="button"
                        nextChangeEvent={loanApplicationSecondApplicantSkipNextEvent}
                    />
                </>
            )}
        </div>
    );
};
export default LoanApplicantSecond;
